import {
  useEffect,
  useId,
  useRef,
  useState,
  type KeyboardEvent as ReactKeyboardEvent,
} from "react";
import { useNavigate } from "react-router-dom";
import { bankIdLaunchUrl, isMobileDevice } from "@care-by-ella/bankid-qr";
import { BankIdQr } from "@/components/BankIdQr";
import { Wordmark } from "@/components/Wordmark";
import { useAuth } from "@/auth/AuthContext";
import {
  devAuthClinician,
  DevAuthError,
  fetchPublicHealth,
  initClinicianBankId,
  pollClinicianBankId,
} from "@/auth/api";

interface QrSeed {
  autoStartToken: string;
  qrStartToken: string;
  qrStartSecret: string;
  initTimeMs: number;
}

// Keep in lockstep with apps/mobile/src/i18n/locales/sv.json. Admin uses
// only Swedish for the partner-clinic launch.
const HINT_COPY: Record<string, string> = {
  outstandingTransaction: "Starta BankID-appen.",
  noClient: "Starta BankID-appen.",
  started: "Söker efter BankID, det kan ta lite tid…",
  userSign: "Signera i BankID-appen.",
  userMrtd: "Slutför skanningen i BankID-appen.",
};

const FAILED_COPY: Record<string, string> = {
  userCancel: "Du avbröt inloggningen.",
  expiredTransaction: "Tiden gick ut. Försök igen.",
  cancelled: "Inloggningen avbröts.",
  unknown: "Något gick fel. Försök igen.",
};

type Status = "idle" | "starting" | "polling" | "failed";

// TES-494 — one dev key per employee role (Ö-56 collapsed the admin aliases,
// so the three segments are the whole vocabulary).
type DevRole = "doctor" | "coach" | "admin";

// The server's error code is kept alongside the copy so the render can decide
// WHERE the message belongs — see ROLE_CHOICE_ERRORS.
type DevError = { code: string | null; message: string };

const DEV_ROLES: { value: DevRole; label: string }[] = [
  { value: "doctor", label: "Läkare" },
  { value: "coach", label: "Coach" },
  { value: "admin", label: "Admin" },
];

const DEV_ROLE_LABEL: Record<DevRole, string> = {
  doctor: "Läkare",
  coach: "Coach",
  admin: "Administratör",
};

const DEV_KEY_PLACEHOLDER: Record<DevRole, string> = {
  doctor: "DEV_DOCTOR_KEY",
  coach: "DEV_COACH_KEY",
  admin: "DEV_ADMIN_KEY",
};

// TES-815 — a mismatched pairing is a problem with the ROLE CHOICE, not with
// the key, so its message is rendered at the segmented control instead of
// under the submit button where it was easy to miss. Every other dev-auth
// failure is about the key itself and stays under the button.
const ROLE_CHOICE_ERRORS = new Set<string>(["role_key_mismatch"]);

const DEV_ERROR_COPY: Record<string, string> = {
  invalid_doctor_key: "Nyckeln stämmer inte för den valda rollen.",
  role_key_mismatch:
    "Nyckeln hör till en annan roll. Välj den roll nyckeln gäller för.",
  doctor_key_not_configured:
    "Inga dev-nycklar är konfigurerade på den här miljön.",
  no_dev_doctor_provisioned:
    "Ingen läkare finns upplagd för kliniken — lägg upp en först.",
};

export function LoginRoute() {
  const navigate = useNavigate();
  const { signIn } = useAuth();
  const [status, setStatus] = useState<Status>("idle");
  const [hint, setHint] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [qrSeed, setQrSeed] = useState<QrSeed | null>(null);
  // TES-965 — a phone or tablet gets the launch link as the primary action.
  const [mobile] = useState(() =>
    isMobileDevice(navigator.userAgent, navigator.maxTouchPoints),
  );

  // CAR-62 — cancellation flag for the BankID poll loop. Without this, an
  // orphan loop kept by a stale closure would keep ticking after the user
  // navigated away or restarted the flow, eventually calling `signIn` +
  // `navigate('/inbox')` on an unmounted page. Set true on unmount and
  // on every fresh `start()` invocation.
  const pollCancelled = useRef(false);
  useEffect(() => {
    return () => {
      pollCancelled.current = true;
    };
  }, []);

  // CAR-54 — only render the dev-login form when the server advertises
  // DEV_AUTH_ENABLED. Production never advertises it (the env schema refuses
  // to boot if the flag is on in NODE_ENV=production).
  const [devAuthEnabled, setDevAuthEnabled] = useState(false);
  const [devKey, setDevKey] = useState("");
  // TES-494 — which view to open. The key is what actually grants the role;
  // this only tells the server which one we expect, so a wrong pairing fails
  // loudly instead of silently signing us in as something else.
  const [devRole, setDevRole] = useState<DevRole>("doctor");
  const [devBusy, setDevBusy] = useState(false);
  const [devError, setDevError] = useState<DevError | null>(null);

  const roleLabelId = useId();
  const roleErrorId = useId();
  // Refs for the roving-tabindex focus moves the radiogroup pattern needs, and
  // so a mismatch can pull focus back up to the choice that caused it.
  const roleRefs = useRef<(HTMLButtonElement | null)[]>([]);

  function selectRole(next: DevRole) {
    setDevRole(next);
    // A mismatch message describes the pairing the user just abandoned, so it
    // must not survive the new choice.
    setDevError(null);
  }

  function focusRole(index: number) {
    selectRole(DEV_ROLES[index].value);
    roleRefs.current[index]?.focus();
  }

  // ARIA radiogroup keyboard contract: arrows move within the group and the
  // selection follows focus; Tab enters/leaves it once, which the roving
  // tabIndex below provides.
  function onRoleKeyDown(e: ReactKeyboardEvent<HTMLButtonElement>, i: number) {
    const last = DEV_ROLES.length - 1;
    if (e.key === "ArrowRight" || e.key === "ArrowDown") {
      e.preventDefault();
      focusRole(i === last ? 0 : i + 1);
    } else if (e.key === "ArrowLeft" || e.key === "ArrowUp") {
      e.preventDefault();
      focusRole(i === 0 ? last : i - 1);
    } else if (e.key === "Home") {
      e.preventDefault();
      focusRole(0);
    } else if (e.key === "End") {
      e.preventDefault();
      focusRole(last);
    }
  }

  useEffect(() => {
    let cancelled = false;
    void fetchPublicHealth().then((h) => {
      if (!cancelled && h?.devAuthEnabled) setDevAuthEnabled(true);
    });
    return () => {
      cancelled = true;
    };
  }, []);

  async function devSignIn() {
    if (!devKey.trim() || devBusy) return;
    setDevBusy(true);
    setDevError(null);
    try {
      const { jwt } = await devAuthClinician(devKey.trim(), devRole);
      // Same handoff as the BankID complete path — signIn exchanges the JWT
      // for the httpOnly session cookie and discards it from JS memory.
      await signIn(jwt);
      // TES-489: "/" is the role-aware redirect (App.tsx HomeRedirect). Both
      // sign-in paths used to hardcode /inbox, which is the DOCTOR's home —
      // so a coach was sent to a page their own role gate refuses, and landed
      // on "Du saknar behörighet" every single login.
      navigate("/", { replace: true });
    } catch (e) {
      const code = e instanceof DevAuthError ? e.message : null;
      setDevError({
        code,
        message:
          (code ? DEV_ERROR_COPY[code] : null) ??
          (e instanceof DevAuthError && e.status === 401
            ? "Ogiltig nyckel för den valda rollen."
            : "Något gick fel. Försök igen."),
      });
      if (code && ROLE_CHOICE_ERRORS.has(code)) {
        // Hand focus back to the role row. The message is rendered up there,
        // above the key field the user was last looking at, so without this a
        // keyboard or screen-reader user would be told the choice is wrong
        // while standing at the wrong end of the form.
        roleRefs.current[
          DEV_ROLES.findIndex((r) => r.value === devRole)
        ]?.focus();
      }
    } finally {
      setDevBusy(false);
    }
  }

  async function start() {
    // Reset the cancellation flag on every fresh start so a second click
    // (after a failed retry) gets a clean loop. The unmount effect still
    // wins if the user navigates away mid-poll.
    pollCancelled.current = false;
    setStatus("starting");
    setError(null);
    setHint(null);
    setQrSeed(null);
    try {
      const init = await initClinicianBankId();
      if (pollCancelled.current) return;
      setQrSeed({
        autoStartToken: init.autoStartToken,
        qrStartToken: init.qrStartToken,
        qrStartSecret: init.qrStartSecret,
        initTimeMs: Date.now(),
      });
      const { orderRef } = init;
      setStatus("polling");
      for (let i = 0; i < 60; i++) {
        if (pollCancelled.current) return;
        await new Promise<void>((r) => setTimeout(() => r(), 1000));
        if (pollCancelled.current) return;
        const data = await pollClinicianBankId(orderRef);
        if (pollCancelled.current) return;
        if (data.status === "complete" && data.jwt) {
          // Hand the JWT straight to signIn, which exchanges it for the
          // httpOnly session cookie and drops it. The token never lands in
          // any persistent JS storage.
          await signIn(data.jwt);
          navigate("/", { replace: true });
          return;
        }
        if (data.status === "failed") {
          setStatus("failed");
          setError(
            (data.hintCode && FAILED_COPY[data.hintCode]) ||
              FAILED_COPY.unknown,
          );
          return;
        }
        if (data.hintCode && HINT_COPY[data.hintCode]) {
          setHint(HINT_COPY[data.hintCode]);
        }
      }
      if (pollCancelled.current) return;
      setStatus("failed");
      setError(FAILED_COPY.expiredTransaction);
    } catch (e) {
      if (pollCancelled.current) return;
      setStatus("failed");
      setError(e instanceof Error ? e.message : FAILED_COPY.unknown);
    }
  }

  const busy = status === "starting" || status === "polling";

  // One message, one place: a role-choice failure is shown at the segmented
  // control, everything else under the submit button. Rendering it in both
  // spots would just teach the eye to ignore one of them.
  const roleError =
    devError && devError.code && ROLE_CHOICE_ERRORS.has(devError.code)
      ? devError
      : null;
  const keyError = roleError ? null : devError;

  return (
    <div className="relative min-h-screen flex flex-col items-center justify-center px-md overflow-hidden">
      <div className="brand-blob -top-32 -right-32 w-[320px] h-[320px]" />
      <div className="brand-blob -bottom-32 -left-32 w-[280px] h-[280px]" />

      <div className="relative z-10 w-full max-w-md flex flex-col items-center gap-lg">
        <Wordmark size="xl" />
        <p className="text-textSecondary text-center">
          Vårdportal — logga in med BankID
        </p>

        <button
          type="button"
          onClick={start}
          disabled={busy}
          className="w-full bg-brand text-textOnBrand font-display tracking-tight text-lg py-md rounded-md hover:opacity-90 disabled:opacity-60 transition"
        >
          {busy ? "Väntar på BankID…" : "Öppna BankID"}
        </button>

        {/* TES-965 — same-device BankID, as on the web login (TES-925).
            On a phone the QR code cannot be scanned from its own screen,
            so the launch link comes first. A real link, not a location
            change: iOS only hands a universal link to the app from a tap. */}
        {status === "polling" && qrSeed ? (
          <a
            href={bankIdLaunchUrl(qrSeed.autoStartToken, mobile)}
            className={
              mobile
                ? "w-full text-center bg-brand text-textOnBrand font-display tracking-tight text-lg py-md rounded-md hover:opacity-90 transition"
                : "text-sm text-brand underline"
            }
          >
            Öppna BankID på den här enheten
          </a>
        ) : null}

        {status === "polling" && qrSeed ? (
          <BankIdQr
            qrStartToken={qrSeed.qrStartToken}
            qrStartSecret={qrSeed.qrStartSecret}
            initTimeMs={qrSeed.initTimeMs}
          />
        ) : null}

        {hint ? <p className="text-sm text-textSecondary">{hint}</p> : null}
        {error ? <p className="text-sm text-danger">{error}</p> : null}

        {devAuthEnabled ? (
          <div className="w-full flex flex-col gap-sm pt-md border-t border-surfaceMuted/60">
            <p className="text-xs text-textSecondary uppercase tracking-wide">
              Dev-inloggning (endast testmiljö)
            </p>
            <div className="flex flex-col gap-xs">
              <p id={roleLabelId} className="text-sm text-textPrimary">
                Vilken roll gäller nyckeln?
              </p>
              <div
                role="radiogroup"
                aria-labelledby={roleLabelId}
                aria-invalid={roleError ? true : undefined}
                aria-describedby={roleError ? roleErrorId : undefined}
                className={`flex rounded-md border overflow-hidden text-sm ${
                  roleError ? "border-danger" : "border-surfaceMuted"
                }`}
              >
                {DEV_ROLES.map((r, i) => {
                  const selected = devRole === r.value;
                  return (
                    <button
                      key={r.value}
                      ref={(el) => {
                        roleRefs.current[i] = el;
                      }}
                      type="button"
                      role="radio"
                      aria-checked={selected}
                      // Roving tabindex: the group is one Tab stop, and the
                      // selected option is the one Tab lands on.
                      tabIndex={selected ? 0 : -1}
                      onClick={() => selectRole(r.value)}
                      onKeyDown={(e) => onRoleKeyDown(e, i)}
                      // The selected segment is filled, not merely tinted —
                      // the old border+text treatment read as three equal
                      // login buttons rather than one answered question.
                      className={`flex-1 py-xs transition ${
                        selected
                          ? "bg-brand text-textOnBrand font-medium"
                          : "bg-surface text-textSecondary hover:bg-surfaceMuted/40"
                      }`}
                    >
                      {r.label}
                    </button>
                  );
                })}
              </div>
              {roleError ? (
                <p
                  id={roleErrorId}
                  role="alert"
                  className="text-sm text-danger"
                >
                  {roleError.message}
                </p>
              ) : null}
            </div>
            <input
              type="password"
              value={devKey}
              onChange={(e) => setDevKey(e.target.value)}
              placeholder={DEV_KEY_PLACEHOLDER[devRole]}
              className="w-full border border-surfaceMuted rounded-md px-md py-sm text-sm"
              autoComplete="off"
            />
            <button
              type="button"
              onClick={devSignIn}
              disabled={!devKey.trim() || devBusy}
              className="w-full bg-surface border border-surfaceMuted text-textPrimary text-sm py-sm rounded-md hover:bg-surfaceMuted/40 disabled:opacity-60 transition"
            >
              {devBusy
                ? "Loggar in…"
                : `Logga in som ${DEV_ROLE_LABEL[devRole].toLowerCase()} (dev)`}
            </button>
            {keyError ? (
              <p role="alert" className="text-sm text-danger">
                {keyError.message}
              </p>
            ) : null}
          </div>
        ) : null}
      </div>
    </div>
  );
}
